import React from 'react';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';
import { FormattedMessage } from 'react-intl';

/**
 * Commonly used layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string?} props.mainColumnClassName add more style rules in addition to css.main
 * @param {string?} props.sideNavClassName add more style rules in addition to css.sideNav
 * @param {ReactNode} props.children
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.sideNav
 * @param {ReactNode?} props.footer
 * @param {boolean?} props.useAccountSettingsNav
 * @param {string?} props.currentPage
 * @returns {JSX.Element} LayoutComposer that expects children to be a function.
 */
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    accountSettingsNavProps,
    intl,
    currentPage,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = containerClassName || css.container;

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    accountSettingsHeader
    main
    footer
  `;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
            </Topbar>
            {useAccountSettingsNav ? <div style={{ gridArea: 'accountSettingsHeader' }} className={css.accountSettingsHeader}>
              <h4>
                Account settings
              </h4>
              <p>Manage your account security and preferences</p>
            </div> : null}
            {currentPage === "InboxPage" ? <div tyle={{ gridArea: 'accountSettingsHeader' }} className={css.accountSettingsHeader}>
              <h4 as="h1" className={css.title}>
                <FormattedMessage id="InboxPage.title" />
              </h4>
            </div> : null}
            <Main as="div" className={containerClasses}>

              <aside
                className={classNames(css.sideNav, sideNavClassName, {
                  [css.sideNavAccountSettings]: useAccountSettingsNav,
                })}
              >
                {useAccountSettingsNav ? (
                  <LayoutWrapperAccountSettingsSideNav
                    accountSettingsNavProps={accountSettingsNavProps}
                    ariaLabel={intl.formatMessage({
                      id: 'LayoutSideNavigation.screenreader.accountNavigation',
                    })}
                  />
                ) : null}
                {sideNavContent}
              </aside>
              <main id="main-content" className={classNames(css.main, mainColumnClassName)}>
                {children}
              </main>
            </Main>
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

export default LayoutSideNavigation;
